import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, AlertCircle, Inbox } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import { Card, Button, Badge, AreaChip } from '../ui-v2';
import { listMisTickets } from './api';

const TABS = [
  { id: 'abiertos', label: 'Abiertas' },
  { id: 'resueltos', label: 'Resueltas' },
  { id: 'sugerencias', label: 'Sugerencias' },
];

// estado → etiqueta + variante de Badge
const ESTADO_META = {
  pendiente: { label: 'Pendiente', variant: 'warning' },
  en_revision: { label: 'En revisión', variant: 'default' },
  en_desarrollo: { label: 'En desarrollo', variant: 'default' },
  resuelto: { label: 'Resuelto', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'destructive' },
  descartado: { label: 'Descartado', variant: 'secondary' },
  recibida: { label: 'Recibida', variant: 'secondary' },
  en_estudio: { label: 'En estudio', variant: 'default' },
  aceptada: { label: 'Aceptada', variant: 'success' },
  implementada: { label: 'Implementada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'destructive' },
};

const PRIORIDAD_LABEL = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Crítica' };

const EMPTY = {
  abiertos: { msg: 'No tienes tickets abiertos. ¡Buena señal!', cta: true },
  resueltos: { msg: 'Aún no tienes tickets resueltos.', cta: false },
  sugerencias: { msg: 'No has enviado sugerencias todavía. ¿Tienes alguna idea?', cta: false },
};

function hace(fecha) {
  try { return formatDistanceToNow(new Date(fecha), { addSuffix: true, locale: es }); }
  catch { return ''; }
}

export default function SoporteDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : 'abiertos';

  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    listMisTickets(tab)
      .then((d) => { setTickets(d.tickets || []); setCounts(d.counts || null); })
      .catch(() => setError('No se pudieron cargar tus tickets.'))
      .finally(() => setCargando(false));
  }, [tab]);

  useEffect(() => { cargar(); }, [cargar]);

  function cambiarTab(id) {
    setSearchParams(id === 'abiertos' ? {} : { tab: id });
  }

  return (
    <div className="px-6 py-6 min-h-full">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900">Mis tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Tickets que has abierto y su estado actual.</p>
        </div>
        <Button onClick={() => navigate('/soporte/nuevo')} className="shrink-0">
          <Plus className="w-4 h-4" /> Nuevo ticket
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => cambiarTab(t.id)}
            className={
              'px-4 py-2 text-sm -mb-px border-b-2 transition-colors ' +
              (tab === t.id
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-800')
            }
          >
            {t.label}
            {counts && <span className="ml-1 text-xs text-slate-400">({counts[t.id] ?? 0})</span>}
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-slate-100 rounded-lg p-4 animate-pulse">
              <div className="h-3 w-24 bg-slate-100 rounded mb-2" />
              <div className="h-4 w-2/3 bg-slate-100 rounded mb-3" />
              <div className="h-3 w-40 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-8 text-center">
          <div className="text-sm text-red-700 flex items-center justify-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
          <Button variant="outline" size="sm" onClick={cargar}>Reintentar</Button>
        </div>
      ) : tickets.length === 0 ? (
        <div className="py-16 text-center">
          <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">{EMPTY[tab].msg}</p>
          {EMPTY[tab].cta && (
            <Button className="mt-4" onClick={() => navigate('/soporte/nuevo')}>
              <Plus className="w-4 h-4" /> Nuevo ticket
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const est = ESTADO_META[t.estado] || { label: t.estado, variant: 'secondary' };
            return (
              <Card
                key={t.id}
                onClick={() => navigate(`/soporte/ticket/${t.id}`)}
                className="p-4 border-slate-200 cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-[11px] font-mono text-slate-400">#{t.numero}</code>
                      <Badge variant="outline" className="text-[10px]">
                        {t.tipo === 'incidencia' ? 'Incidencia' : 'Sugerencia'}
                      </Badge>
                    </div>
                    <div className="text-sm font-medium text-slate-900 truncate">{t.titulo}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {t.categoria && <AreaChip area={t.categoria.color || 'otros'}>{t.categoria.nombre}</AreaChip>}
                      {t.tipo === 'incidencia' && t.prioridad && (
                        <span className="text-[11px] text-slate-500">Prioridad: {PRIORIDAD_LABEL[t.prioridad] || t.prioridad}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Badge variant={est.variant}>{est.label}</Badge>
                    <span className="text-[11px] text-slate-400">{hace(t.createdAt)}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
